import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Translation keys. Hosts can pass a locale and override any message.
 * Placeholders use `{name}`.
 */
import { TOOL_MESSAGES_EN, TOOL_MESSAGES_TR } from '@dotloomjs/sdk';
import { createContext, useContext, useMemo } from 'react';
export const en = {
    'app.title': 'Dotloom editor',
    'menu.file': 'File',
    'menu.new': 'New',
    'menu.open': 'Open…',
    'menu.save': 'Save .dotl',
    'menu.exportSvg': 'Export SVG',
    'menu.exportDxf': 'Export DXF',
    'menu.exportPng': 'Export PNG',
    'menu.undo': 'Undo',
    'menu.redo': 'Redo',
    'menu.commands': 'Commands',
    'menu.theme': 'Theme',
    'theme.light': 'Light',
    'theme.dark': 'Dark',
    'theme.system': 'System',
    'panel.layers': 'Layers',
    'panel.objects': 'Objects',
    'panel.inspector': 'Properties',
    'panel.constraints': 'Rules',
    'panel.document': 'Document',
    'layers.add': 'Add layer',
    'layers.visible': 'Visible',
    'layers.locked': 'Locked',
    'layers.active': 'Draw on this layer',
    'layers.rename': 'Rename layer',
    'layers.remove': 'Remove layer',
    'layers.newName': 'Layer {n}',
    'objects.filter': 'Filter objects',
    'objects.empty': 'No objects yet. Pick a drawing tool or open a file.',
    'objects.count': '{n} objects',
    'objects.readOnly': 'read-only',
    'inspector.none': 'Nothing selected. Select an object on the canvas or in the list.',
    'inspector.many': '{n} objects selected',
    'inspector.name': 'Name',
    'inspector.layer': 'Layer',
    'inspector.locked': 'Locked',
    'inspector.hidden': 'Hidden',
    'inspector.type': 'Type',
    'inspector.lock': 'Lock value',
    'inspector.unlock': 'Unlock value',
    'inspector.invalid': 'Enter a number, optionally with a unit (mm, cm, m, in, ft).',
    'inspector.nearest': 'Use nearest allowed value {value}',
    'inspector.unitHint': 'Values in {unit}',
    'inspector.readOnly.missingPlugin': 'This object needs the plugin type "{type}", which is not installed. It is shown read-only and its data is kept.',
    'inspector.readOnly.disabledPlugin': 'The plugin providing "{type}" is disabled. Enable it to edit this object.',
    'inspector.readOnly.newerVersion': 'This object was made with a newer version of "{type}". Update the plugin to edit it.',
    'inspector.delete': 'Delete',
    'inspector.group': 'Group',
    'document.title': 'Title',
    'document.unit': 'Display unit',
    'document.grid': 'Grid spacing',
    'rules.status.solved': 'All rules are satisfied.',
    'rules.status.underconstrained': 'Rules are satisfied; {dof} degrees of freedom remain.',
    'rules.status.conflicting': 'Rules conflict.',
    'rules.status.notConverged': 'The solver did not converge.',
    'rules.status.cancelled': 'Solving was cancelled.',
    'rules.status.unsupported': 'A rule is not supported.',
    'rules.none': 'No rules on the selection.',
    'rules.enabled': 'Enabled',
    'rules.remove': 'Remove rule',
    'rules.add': 'Add rule',
    'rules.horizontal': 'Horizontal',
    'rules.vertical': 'Vertical',
    'rules.length': 'Fixed length',
    'rules.parallel': 'Parallel',
    'rules.perpendicular': 'Perpendicular',
    'rules.equalLength': 'Equal length',
    'rules.concentric': 'Concentric',
    'rules.equalRadius': 'Equal radius',
    'rules.coincident': 'Coincident ends',
    'error.dismiss': 'Dismiss',
    'error.title': 'The change was not applied',
    'error.conflict': 'It would break these rules:',
    'error.load': 'The file could not be opened',
    'error.gpu.title': 'Graphics could not start',
    'error.details': 'Technical details',
    'error.gpu.body': 'Drawing on the canvas is unavailable in this browser. You can still edit objects in the panels, open and save files.',
    'missing.title': 'Missing plugins',
    'missing.body': 'These object types are not available: {types}. Their objects are read-only and their data is kept.',
    'recovery.title': 'Restore unsaved work?',
    'recovery.body': '"{name}" has changes from {time} that were not saved to a file.',
    'recovery.restore': 'Restore',
    'recovery.discard': 'Discard',
    'status.saved': 'Saved',
    'status.saving': 'Saving…',
    'status.unsaved': 'Unsaved changes',
    'status.autosaved': 'Autosaved {time}',
    'status.autosaveFailed': 'Autosave failed: {message}',
    'status.busy': 'Solving…',
    'status.cancel': 'Cancel',
    'status.snap': 'Snap',
    'status.grid': 'Grid',
    'status.zoom': 'Zoom',
    'status.input': 'Typed value',
    'empty.title': 'Empty drawing',
    'empty.body': 'Choose a tool on the left, or open a .dotl, SVG or DXF file.',
    'palette.placeholder': 'Type a command…',
    'palette.empty': 'No matching commands',
    'palette.tool': 'Tool: {name}',
    'action.selectAll': 'Select all',
    'action.fit': 'Zoom to fit',
    'action.export': 'Export {ext} ({plugin})',
    ...TOOL_MESSAGES_EN,
};
export const tr = {
    'app.title': 'Dotloom düzenleyici',
    'menu.file': 'Dosya',
    'menu.new': 'Yeni',
    'menu.open': 'Aç…',
    'menu.save': '.dotl kaydet',
    'menu.exportSvg': 'SVG dışa aktar',
    'menu.exportDxf': 'DXF dışa aktar',
    'menu.exportPng': 'PNG dışa aktar',
    'menu.undo': 'Geri al',
    'menu.redo': 'Yinele',
    'menu.commands': 'Komutlar',
    'menu.theme': 'Tema',
    'theme.light': 'Açık',
    'theme.dark': 'Koyu',
    'theme.system': 'Sistem',
    'panel.layers': 'Katmanlar',
    'panel.objects': 'Nesneler',
    'panel.inspector': 'Özellikler',
    'panel.constraints': 'Kurallar',
    'panel.document': 'Belge',
    'layers.add': 'Katman ekle',
    'layers.visible': 'Görünür',
    'layers.locked': 'Kilitli',
    'layers.active': 'Bu katmana çiz',
    'layers.rename': 'Katmanı yeniden adlandır',
    'layers.remove': 'Katmanı kaldır',
    'layers.newName': 'Katman {n}',
    'objects.filter': 'Nesneleri filtrele',
    'objects.empty': 'Henüz nesne yok. Bir çizim aracı seçin veya dosya açın.',
    'objects.count': '{n} nesne',
    'objects.readOnly': 'salt okunur',
    'inspector.none': 'Seçim yok. Tuvalde veya listede bir nesne seçin.',
    'inspector.many': '{n} nesne seçili',
    'inspector.name': 'Ad',
    'inspector.layer': 'Katman',
    'inspector.locked': 'Kilitli',
    'inspector.hidden': 'Gizli',
    'inspector.type': 'Tür',
    'inspector.lock': 'Değeri kilitle',
    'inspector.unlock': 'Kilidi kaldır',
    'inspector.invalid': 'Bir sayı girin; isterseniz birimle (mm, cm, m, in, ft).',
    'inspector.nearest': 'İzin verilen en yakın değeri kullan: {value}',
    'inspector.unitHint': 'Değerler {unit} cinsinden',
    'inspector.readOnly.missingPlugin': 'Bu nesne kurulu olmayan "{type}" eklenti türünü gerektiriyor. Salt okunur gösteriliyor, verisi korunuyor.',
    'inspector.readOnly.disabledPlugin': '"{type}" türünü sağlayan eklenti kapalı. Düzenlemek için etkinleştirin.',
    'inspector.readOnly.newerVersion': 'Bu nesne "{type}" türünün daha yeni bir sürümüyle oluşturulmuş. Düzenlemek için eklentiyi güncelleyin.',
    'inspector.delete': 'Sil',
    'inspector.group': 'Grupla',
    'document.title': 'Başlık',
    'document.unit': 'Görüntü birimi',
    'document.grid': 'Izgara aralığı',
    'rules.status.solved': 'Tüm kurallar sağlanıyor.',
    'rules.status.underconstrained': 'Kurallar sağlanıyor; {dof} serbestlik derecesi kaldı.',
    'rules.status.conflicting': 'Kurallar çelişiyor.',
    'rules.status.notConverged': 'Çözücü yakınsamadı.',
    'rules.status.cancelled': 'Çözüm iptal edildi.',
    'rules.status.unsupported': 'Desteklenmeyen bir kural var.',
    'rules.none': 'Seçimde kural yok.',
    'rules.enabled': 'Etkin',
    'rules.remove': 'Kuralı kaldır',
    'rules.add': 'Kural ekle',
    'rules.horizontal': 'Yatay',
    'rules.vertical': 'Dikey',
    'rules.length': 'Sabit uzunluk',
    'rules.parallel': 'Paralel',
    'rules.perpendicular': 'Dik',
    'rules.equalLength': 'Eşit uzunluk',
    'rules.concentric': 'Eş merkezli',
    'rules.equalRadius': 'Eşit yarıçap',
    'rules.coincident': 'Uçları birleştir',
    'error.dismiss': 'Kapat',
    'error.title': 'Değişiklik uygulanmadı',
    'error.conflict': 'Şu kuralları bozacaktı:',
    'error.load': 'Dosya açılamadı',
    'error.gpu.title': 'Grafik başlatılamadı',
    'error.details': 'Teknik ayrıntılar',
    'error.gpu.body': 'Bu tarayıcıda tuval çizimi kullanılamıyor. Nesneleri panellerden düzenleyebilir, dosya açıp kaydedebilirsiniz.',
    'missing.title': 'Eksik eklentiler',
    'missing.body': 'Şu nesne türleri kullanılamıyor: {types}. Nesneleri salt okunur, verileri korunuyor.',
    'recovery.title': 'Kaydedilmemiş çalışma geri yüklensin mi?',
    'recovery.body': '"{name}" belgesinde {time} tarihinden kalan, dosyaya kaydedilmemiş değişiklikler var.',
    'recovery.restore': 'Geri yükle',
    'recovery.discard': 'Vazgeç',
    'status.saved': 'Kaydedildi',
    'status.saving': 'Kaydediliyor…',
    'status.unsaved': 'Kaydedilmemiş değişiklikler',
    'status.autosaved': 'Otomatik kaydedildi {time}',
    'status.autosaveFailed': 'Otomatik kayıt başarısız: {message}',
    'status.busy': 'Çözülüyor…',
    'status.cancel': 'İptal',
    'status.snap': 'Yakala',
    'status.grid': 'Izgara',
    'status.zoom': 'Yakınlaştırma',
    'status.input': 'Girilen değer',
    'empty.title': 'Boş çizim',
    'empty.body': 'Soldan bir araç seçin veya .dotl, SVG ya da DXF dosyası açın.',
    'palette.placeholder': 'Bir komut yazın…',
    'palette.empty': 'Eşleşen komut yok',
    'palette.tool': 'Araç: {name}',
    'action.selectAll': 'Tümünü seç',
    'action.fit': 'Sığdır',
    'action.export': '{ext} dışa aktar ({plugin})',
    ...TOOL_MESSAGES_TR,
};
export const LOCALES = { en, tr };
const I18nCtx = createContext((k) => en[k] ?? k);
export function makeTranslate(locale, overrides = {}) {
    const base = LOCALES[locale] ?? LOCALES[locale.split('-')[0] ?? ''] ?? en;
    return (key, params) => {
        const msg = overrides[key] ?? base[key] ?? en[key] ?? key;
        return params ? msg.replace(/\{(\w+)\}/g, (_, n) => String(params[n] ?? `{${n}}`)) : msg;
    };
}
export function I18nProvider(props) {
    const t = useMemo(() => makeTranslate(props.locale ?? 'en', props.messages), [props.locale, props.messages]);
    return _jsx(I18nCtx.Provider, { value: t, children: props.children });
}
/** Translation function of the nearest `I18nProvider`. */
export function useT() {
    return useContext(I18nCtx);
}
//# sourceMappingURL=i18n.js.map